import moment from "moment";
import { get, writable } from "svelte/store";
import DownsampleRate from "../Enums/DownsampleRate";
import LoadState from "../Enums/LoadState";
import GeoHelper from "../Helpers/GeoHelper";
import { formatTime } from "../Helpers/Misc/timeHelper";
import StringHelper from "../Helpers/StringHelper";
import ValidationHelper from "../Helpers/ValidationHelper";
import MapDataSource from "../Map/Enums/MapDataSource";
import MapLayer from "../Map/Enums/MapLayer";
import MapLayerGroup from "../Map/Enums/MapLayerGroup";
import MapLayerManager from "../Map/MapLayerManager";
import { Layout, Paint } from "../Map/MapLayerStyles";
import PopupStore from "../Map/Popup/PopupStore";
import MapStore from "../Map/Store";
import AircraftProperty from "../Models/Enums/AircraftProperty";
import Vessel from "../Models/Vessel";
import VesselGroup from "../Models/VesselGroup";

const AircraftHistoryStore = {
	meta: {
		loadState: writable(LoadState.INIT),
		progress: writable(0),
		error: writable(""),
		lastUpdated: writable(0),
		mapInitted: writable(false)
	},
	results: {
		raw: writable({
			aircraft: []
		}),
		points: {
			raw: writable([]),
			filtered: writable([])
		},
		lines: {
			raw: writable([]),
			filtered: writable([])
		},
		aircrafts: writable([])
	},
	aircraftGroup: new VesselGroup(),
	query: {
		inputString: writable(""),
		inputValid: writable(false),
		inputLength: writable(0), // length of imos
		inputError: writable(false), // showValidationText
		validInputs: writable([]),
		invalidInputs: writable([]),
		fromDatetime: writable(new Date()),
		toDatetime: writable(new Date()),
		downsampling: writable(DownsampleRate.MINUTE)
	},
	filters: {
		layers: {
			heatmap: {
				enabled: writable(true)
			},
			positions: {
				enabled: writable(true)
			},
			journeys: {
				enabled: writable(true)
			},
			aircraft: {
				enabled: writable(true)
			}
		},
		selected: {
			icaoAddresses: writable([])
		},
		filterString: writable("")
	},
	actions: {
		setToEndOfDay: () => {
			// This function would be appropriate, except, the datepicker always sets the time to 00:00:00
			// So until that (external, library) component is replaced, this is useless. ^AG
			// console.log("setToEndOfDay");
			const endDatetime = moment(get(AircraftHistoryStore.query.toDatetime)).endOf("day");

			// console.log("endDatetime", endDatetime.toDate());
			// console.log("endDatetime", endDatetime.toDate().toISOString());

			AircraftHistoryStore.query.toDatetime.set(endDatetime.toDate());
			AircraftHistoryStore.meta.lastUpdated.set(Date.now());
		},
		initMap: (map) => {
			if (get(AircraftHistoryStore.meta.mapInitted)) {
				return;
			}
			AircraftHistoryStore.actions.setToEndOfDay();

			map.addSource(MapDataSource.AIRCRAFT_HISTORY_POINTS, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addSource(MapDataSource.AIRCRAFT_HISTORY_LINES, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addSource(MapDataSource.AIRCRAFT_HISTORY_SNAPSHOT, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});

			map.addLayer(
				{
					id: MapLayer.AIRCRAFT_HISTORY_HEATMAP,
					type: "heatmap",
					source: MapDataSource.AIRCRAFT_HISTORY_POINTS,
					maxzoom: 9,
					paint: Paint.heatmap
				},
				"waterway-label"
			);
			map.addLayer(
				{
					id: MapLayer.AIRCRAFT_HISTORY_LINES,
					type: "line",
					source: MapDataSource.AIRCRAFT_HISTORY_LINES,
					paint: Paint.vesselTrails
				},
				"waterway-label"
			);
			map.addLayer(
				{
					id: MapLayer.AIRCRAFT_HISTORY_POINTS,
					type: "symbol",
					source: MapDataSource.AIRCRAFT_HISTORY_POINTS,
					minzoom: 7,
					layout: Layout.historic_query
				},
				"waterway-label"
			);
			map.addLayer(
				{
					id: MapLayer.AIRCRAFT_HISTORY_SNAPSHOT,
					source: MapDataSource.AIRCRAFT_HISTORY_SNAPSHOT,
					type: "symbol",
					layout: Layout.selectableAircraft
				},
				"waterway-label"
			);

			MapLayerManager.addLayerGroup(MapLayerGroup.AIRCRAFT_HISTORY, [
				MapLayer.AIRCRAFT_HISTORY_HEATMAP,
				MapLayer.AIRCRAFT_HISTORY_LINES,
				MapLayer.AIRCRAFT_HISTORY_POINTS,
				MapLayer.AIRCRAFT_HISTORY_SNAPSHOT
			], [
				MapDataSource.AIRCRAFT_HISTORY_POINTS,
				MapDataSource.AIRCRAFT_HISTORY_LINES,
				MapDataSource.AIRCRAFT_HISTORY_SNAPSHOT
			]);

			// Point hovers
			const enter = (e) => {
				const props = e.features[0].properties;
				const coords = e.features[0].geometry.coordinates;

				let html = "";
				const registration = props[AircraftProperty.REGISTRATION];

				let thisAircraft = null;
				if (typeof props[AircraftProperty.FLIGHT_NUMBER] !== "undefined") {
					thisAircraft = AircraftHistoryStore.aircraftGroup.find({
						[AircraftProperty.FLIGHT_NUMBER]: props[AircraftProperty.FLIGHT_NUMBER]
					});
				}
				else if (typeof props[AircraftProperty.REGISTRATION] !== "undefined") {
					thisAircraft = AircraftHistoryStore.aircraftGroup.find({
						[AircraftProperty.REGISTRATION]: props[AircraftProperty.REGISTRATION]
					});
				}

				html += `<strong>${registration}</strong><br/>`;

				// let flightNumber = find by flight

				html += `Flight <i>${Vessel.formatUndefined(props[AircraftProperty.FLIGHT_NUMBER])}</i> operated by ${Vessel.formatUndefined(props[AircraftProperty.FLIGHT_OPERATOR])}<br/><br/>`;
				html += "<table>";

				html += `<tr>
							<td><b>Time:</b></td>
							<td>${formatTime(props[AircraftProperty.TIMESTAMP])}</td>
						 </tr>`;

				if (typeof props[AircraftProperty.EQUIPMENT] !== "undefined") {
					html += `<tr>
								<td><b>Aircraft:</b></td>
								<td>${props[AircraftProperty.EQUIPMENT]}</td>
							</tr>`;
				}
				if (typeof props[AircraftProperty.ALTITUDE] !== "undefined") {
					html += `<tr>
								<td><b>Altitude:</b></td>
								<td>${props[AircraftProperty.ALTITUDE]} ft</td>
							</tr>`;
				}
				if (typeof props[AircraftProperty.ALTITUDE_BARO] !== "undefined") {
					html += `<tr>
								<td><b>Altitude:</b></td>
								<td>${props[AircraftProperty.ALTITUDE_BARO]} baro</td>
							</tr>`;
				}
				if (typeof props[AircraftProperty.CALL_SIGN] !== "undefined") {
					html += `<tr>
								<td><b>Call Sign:</b></td>
								<td>${props[AircraftProperty.CALL_SIGN]}</td>
							</tr>`;
				}

				html += "</table>";

				PopupStore.actions.customHtml(html, 400, coords, true);
			};

			const move = enter;
			const leave = (e) => {
				PopupStore.actions.custom({}, null, false);
			};

			map.on("mouseenter", MapLayer.AIRCRAFT_HISTORY_POINTS, (e) => {
				enter(e);
			});
			map.on("mousemove", MapLayer.AIRCRAFT_HISTORY_POINTS, (e) => {
				move(e);
			});
			map.on("mouseleave", MapLayer.AIRCRAFT_HISTORY_POINTS, (e) => {
				leave(e);
			});
			map.on("click", MapLayer.AIRCRAFT_HISTORY_POINTS, event => {
				event.preventDefault();
				const features = map.queryRenderedFeatures(event.point, {
					layers: [MapLayer.AIRCRAFT_HISTORY_POINTS]
				});
				// console.log("features.length", features.length);
				if (features.length) {
					const feature = features[0];
					const featureTimeStamp = feature.properties[AircraftProperty.TIMESTAMP];
					// console.log("featureTimeStamp", featureTimeStamp);
					if (featureTimeStamp) {
						AircraftHistoryStore.aircraftGroup.timeSliderData.value.set(moment(new Date(featureTimeStamp)));
					}
				}
			});
			map.on("click", MapLayer.AIRCRAFT_HISTORY_SNAPSHOT, event => {
				event.preventDefault();
				AircraftHistoryStore.aircraftGroup.selectSingle(map, event, [MapLayer.AIRCRAFT_HISTORY_SNAPSHOT], [AircraftProperty.ICAO_ADDRESS]);
			});

			AircraftHistoryStore.meta.mapInitted.set(true);
		},
		deinitMap: () => {
			if (!get(AircraftHistoryStore.meta.mapInitted)) {
				return;
			}
			MapLayerManager.removeLayerGroup(MapLayerGroup.AIRCRAFT_HISTORY);
			AircraftHistoryStore.meta.mapInitted.set(false);
		},
		updateGeojsonData: () => {
			const aircrafts = get(AircraftHistoryStore.results.aircrafts);

			const points = [];
			const lines = [];
			for (let i = 0; i < aircrafts.length; i++) {
				const aircraft = aircrafts[i];
				const lineCoords = [];
				// points.push(aircraft.feature);
				// console.log("aircraft.historicPath.points", aircraft.historicPath.points);
				for (let j = 0; j < aircraft.historicPath.points.length; j++) {
					points.push(aircraft.historicPath.points[j]);
				}

				// add lines data
				lineCoords.push(aircraft.coordinates);
				for (let j = 0; j < aircraft.historicPath.points.length; j++) {
					const pointCoords = aircraft.historicPath.points[j].geometry.coordinates;
					lineCoords.push(pointCoords);
				}
				// Geojson data
				const coordsLineFeature = GeoHelper.fixLineWrapAround({
					type: "Feature",
					properties: {
						icaoAddress: aircraft.icaoAddress,
						lineColor: aircraft.lineColor
					},
					geometry: {
						type: "LineString",
						coordinates: lineCoords
					}
				});
				lines.push(coordsLineFeature);
			}
			AircraftHistoryStore.results.points.raw.set(points);
			AircraftHistoryStore.results.lines.raw.set(lines);
		},
		updateMapSources: () => {
			const map = get(MapStore.mapbox.instance);
			const points = get(AircraftHistoryStore.results.points.raw);
			const lines = get(AircraftHistoryStore.results.lines.raw);

			if (!get(AircraftHistoryStore.meta.mapInitted)) {
				console.log("🔄 Refreshing AH sources REJECTED (no map layers)");
				return;
			}

			const aircrafts = get(AircraftHistoryStore.results.aircrafts);
			// for (let i = 0; i < aircrafts.length; i++) {
			// 	points.push(aircrafts[i].coordinates);
			// }

			map.getSource(MapDataSource.AIRCRAFT_HISTORY_POINTS).setData(GeoHelper.emptyGeoJson(points));
			map.getSource(MapDataSource.AIRCRAFT_HISTORY_LINES).setData(GeoHelper.emptyGeoJson(lines));

			const date = get(AircraftHistoryStore.aircraftGroup.timeSliderData.value);
			const aircraftToDisplay = [];
			aircrafts.forEach((aircraft) => {
				aircraft.updateSnapshot(date);
				// if (aircraft.visible) {
				aircraftToDisplay.push(aircraft.currentFeature);
				// console.log("aircraft", aircraft, aircraft.currentFeature);
				// }
			});
			// console.log("aircraftToDisplay", aircraftToDisplay);
			map.getSource(MapDataSource.AIRCRAFT_HISTORY_SNAPSHOT).setData(GeoHelper.emptyGeoJson(aircraftToDisplay));

			// console.log("aircraftToDisplay", aircraftToDisplay);
			// console.log("points", points);
		}
	},
	helpers: {
		onICAOTextareaInput: (event) => {
			console.log("onICAOTextareaInput", event);
			AircraftHistoryStore.helpers.onICAOTextareaInputValue(event.target.value);
		},
		onICAOTextareaInputValue: (newValue) => {
			let allValuesValid = true; // Default to true
			AircraftHistoryStore.query.invalidInputs.set([]);
			if (newValue.length > 0) {
				// don't try parse empty
				const splitValues = StringHelper.splitICAOsByCommaOrSpace(newValue);

				if (splitValues.length > 0) {
					// if there's something other than commas and spaces...
					console.log(splitValues);
					const invalids = [];
					for (let i = 0; i < splitValues.length; i++) {
						const val = splitValues[i];
						if (!ValidationHelper.isValidICAO(val)) {
							// set back to false if any don't match
							allValuesValid = false;
							AircraftHistoryStore.query.inputError.set(true); // showValidationText = true;

							invalids.push(val);
						}
					}
					AircraftHistoryStore.query.invalidInputs.set(invalids);
					if (allValuesValid) {
						AircraftHistoryStore.query.validInputs.set(splitValues); // All is good, set in store
						AircraftHistoryStore.query.inputLength.set(splitValues.length);
					}
				}
				else {
					AircraftHistoryStore.query.validInputs.update(existing => [
						...existing,
						newValue
					]);
					allValuesValid = false;
				}
			}
			AircraftHistoryStore.query.inputValid.set(allValuesValid);
			if (allValuesValid) {
				AircraftHistoryStore.query.inputError.set(false); // showValidationText = false;
			}
		},

		onICAOTextareaLoseFocus: () => {
			console.log("onImoMMSITextareaLoseFocus");
			if (!get(AircraftHistoryStore.query.inputValid) && get(AircraftHistoryStore.query.inputLength) > 0) {
				AircraftHistoryStore.query.inputError.set(true); // showValidationText = true;
			}
		}
	}
};
export default AircraftHistoryStore;
